import { first } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useShallowEqualSelector } from '../../../common/hooks/useShallowEqualSelector';
import { useEditable } from '../../../common/modal/EditableContext';
import {
  getUpdateEditedBookingGood,
  getUpdateInitialBookingGood,
  getUpdateIntermodalBookingGoodValidationErrors,
  getUpdateIntermodalBookingIsSubmitting,
  modifyUpdateIntermodalBookingGood,
  type UpdateIntermodalBookingGoodFormModel,
} from '../../../services/intermodal/updateIntermodalReducer';
import type { StoreState } from '../../../store/storeState';
import {
  type TrackerAction,
  transformUpdateIntermodalGoodsFieldToTrackerAction,
} from '../../simple-create/hooks/UseTrackingTextInput';

interface useIntermodalGoodsFieldProps<TFormState, Key extends keyof TFormState> {
  disabled: boolean;
  editedValue: TFormState[Key] | undefined;
  initialValue: TFormState[Key] | undefined;
  onChange: (value: TFormState[Key]) => void;
  revertChange: () => void;
  trackerAction: TrackerAction;
  validationError?: string;
}

export const useUpdateIntermodalGoodsField = <
  Key extends keyof UpdateIntermodalBookingGoodFormModel,
>(
  goodId: string,
  name: Key,
): useIntermodalGoodsFieldProps<UpdateIntermodalBookingGoodFormModel, Key> => {
  const editable = useEditable();

  const mapState = useCallback(
    (state: StoreState) => {
      const initialFormState = getUpdateInitialBookingGood(state, goodId);
      const editedFormState = getUpdateEditedBookingGood(state, goodId);
      const validationErrors = getUpdateIntermodalBookingGoodValidationErrors(state, goodId, name);
      const isSubmitting = getUpdateIntermodalBookingIsSubmitting(state);
      return {
        editedValue: editedFormState?.[name],
        initialValue: initialFormState?.[name],
        validationError: first(validationErrors),
        isSubmitting,
      };
    },
    [goodId, name],
  );

  const { editedValue, initialValue, isSubmitting, validationError } =
    useShallowEqualSelector(mapState);
  const disabled = isSubmitting || !editable;

  const trackerAction = transformUpdateIntermodalGoodsFieldToTrackerAction(name);

  const dispatch = useDispatch();
  const onChange = useCallback<(value: UpdateIntermodalBookingGoodFormModel[Key]) => void>(
    newValue => {
      if (editedValue !== newValue) {
        dispatch(
          modifyUpdateIntermodalBookingGood({ updatedFields: { [name]: newValue }, id: goodId }),
        );
      }
    },
    [editedValue, dispatch, name, goodId],
  );

  const revertChange = useCallback(
    () => () => {
      if (initialValue) {
        onChange(initialValue);
      }
    },
    [initialValue, onChange],
  );

  return {
    editedValue,
    initialValue,
    onChange,
    revertChange,
    disabled,
    validationError,
    trackerAction,
  };
};
